// src/pages/ModelApp.tsx
import { Helmet } from "react-helmet";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { initTelegram } from "../telegram";
import { models } from "../models";

import { LEVELS, clamp } from "../customization/levels";
import { buildPairs } from "../utils/minioUrls";
import type { ImageEntry } from "../types";

import { useTelegramPageGuards } from "../hooks/useTelegramPageGuards";
import { confirmWA } from "../utils/confirmWA";

import { Modal } from "../components/Modal";
import { MemoryBoard } from "../components/MemoryBoard";
import { Gallery } from "../components/Gallery";
import { Confetti } from "../components/Confetti";
import { Toolbar } from "../components/Toolbar";
import { ShopModal, Basket } from "../components/ShopModal";
import { PaymentSheet } from "../components/PaymentSheet";

const LS_TIME_BAL = "extraTimeBalanceSec";
const LS_HINT_BAL = "hintBalanceCount";
const LS_PROGRESS = (model?: string, album?: string) =>
  `progress:${model ?? "unknown"}:${album ?? "default"}`;

function tg() {
  return (window as any)?.Telegram?.WebApp;
}

export default function ModelApp() {
  // Init Telegram SDK
  useEffect(() => { initTelegram(); }, []);

  // Params: support both /:modelName/a/:album and /:modelName/:album
  const { modelName, album: albumParamRaw } = useParams<{ modelName?: string; album?: string }>();
  const nav = useNavigate();

  // Normalize album param if route was /:modelName/a/:album
  const pathSegs =
    typeof window !== "undefined" ? window.location.pathname.split("/").filter(Boolean) : [];
  const lastSeg = pathSegs[pathSegs.length - 1];
  const albumParam = albumParamRaw === "a" ? lastSeg : albumParamRaw;

  // Resolve model & album meta
  const model = models[modelName ?? "" as keyof typeof models] as any;
  const albumObj =
    model && albumParam ? (model.albums || []).find((a: any) => a.id === albumParam) : undefined;

  const albumId = albumObj?.id ?? albumParam ?? "default";
  const albumTitle = albumObj?.title ?? albumId;
  const totalLevels = albumObj?.totalLevels ?? LEVELS.length;

  // ---- Progress per album ----
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    if (!modelName || !albumId) return;
    const saved = localStorage.getItem(LS_PROGRESS(modelName, albumId));
    const v = saved ? parseInt(saved, 10) : 0;
    setProgress(clamp(v, 0, totalLevels));
  }, [modelName, albumId, totalLevels]);

  useEffect(() => {
    if (!modelName || !albumId) return;
    localStorage.setItem(LS_PROGRESS(modelName, albumId), String(progress));
  }, [progress, modelName, albumId]);

  // ---- Wallets ----
  const [extraBalanceSec, setExtraBalanceSec] = useState<number>(() => {
    const v = localStorage.getItem(LS_TIME_BAL);
    return v ? parseInt(v, 10) : 0;
  });
  const [hintBalance, setHintBalance] = useState<number>(() => {
    const v = localStorage.getItem(LS_HINT_BAL);
    return v ? parseInt(v, 10) : 0;
  });
  useEffect(() => { localStorage.setItem(LS_TIME_BAL, String(extraBalanceSec)); }, [extraBalanceSec]);
  useEffect(() => { localStorage.setItem(LS_HINT_BAL, String(hintBalance)); }, [hintBalance]);

  // ---- Game UI state ----
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [externalAddedSeconds, setExternalAddedSeconds] = useState<number>(0);
  useEffect(() => { setExternalAddedSeconds(0); }, [playingIndex, modalOpen]);

  // ---- Shop/Payment state ----
  const [shopOpen, setShopOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentBasket, setPaymentBasket] = useState<Basket | null>(null);

  // ---- Build images (album-aware) ----
  const imageEntries: ImageEntry[] = useMemo(() => {
    if (!model || !albumObj) return [];
    const pairs = buildPairs(model.id, albumId, totalLevels);
    return pairs.map((p) => ({ blurred: p.blur, clear: p.clear }));
  }, [model, albumObj, albumId, totalLevels]);

  // ------------------------------------------------------------------
  // Close/back guards (Telegram) — MUST be called on every render
  // ------------------------------------------------------------------
  const guardActive = modalOpen || paymentOpen;

  useTelegramPageGuards({
    shouldConfirmClose: guardActive,
    backConfirmMessage: "Quit this game? Your current round will be closed.",
    onConfirmBack: () => {
      if (paymentOpen) { setPaymentOpen(false); return; }
      if (modalOpen) { setModalOpen(false); document.body.style.overflow = ""; return; }
      nav(`/${model?.id ?? ""}`);
    },
  });

  // ---- Early guards (after ALL hooks are called) ----
  if (!modelName || !model) {
    return <div style={{ padding: 24 }}>Model not found</div>;
  }
  if (!albumParam || !albumObj) {
    return <div style={{ padding: 24 }}>Album not found</div>;
  }

  // ---- Game logic ----
  const unlockedIndex = Math.min(progress, Math.max(0, totalLevels - 1));
  const activeIdx = playingIndex ?? unlockedIndex;
  const cfgIdx = clamp(activeIdx, 0, Math.min(totalLevels, LEVELS.length) - 1);
  const levelCfg = LEVELS[cfgIdx];
  const solvedActive = activeIdx < progress;

  const startLevel = (idx: number) => {
    if (idx > unlockedIndex) return;
    setExternalAddedSeconds(0);
    setPlayingIndex(idx);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };
  const closeLevel = () => {
    setModalOpen(false);
    document.body.style.overflow = "";
  };
  const handleCompleteLevel = () => {
    if (playingIndex === null) return;
    const current = playingIndex;
    setShowConfetti(true);
    window.setTimeout(() => {
      setShowConfetti(false);
      const nextProgress = current + 1;
      if (progress < nextProgress) setProgress(nextProgress);
    }, 1100);
  };
  const goToNextLevel = () => {
    if (playingIndex === null) return;
    const next = playingIndex + 1;
    setExternalAddedSeconds(0);
    if (next < totalLevels) {
      setPlayingIndex(next);
    } else {
      setModalOpen(false);
      document.body.style.overflow = "";
      alert("Congrats! You’ve unlocked all pictures 🎉");
    }
  };

  // Toolbar actions
  const doRestartAll = () => {
    setProgress(0);
    setPlayingIndex(0);
    setExternalAddedSeconds(0);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };
  const handleRestartAll = async () => {
    const ok = await confirmWA({
      title: "Restart from Level 1?",
      message: `This will reset your progress for "${albumTitle}".`,
      okText: "Restart",
      cancelText: "Cancel",
    });
    if (ok) doRestartAll();
  };

  const handleHome = async () => {
    if (!guardActive) {
      nav(`/${model.id}`);
      return;
    }
    const ok = await confirmWA({
      title: "Quit this game?",
      message: "Your current round will be closed and you’ll go back to Home.",
      okText: "Leave",
      cancelText: "Stay",
    });
    if (!ok) return;

    if (paymentOpen) setPaymentOpen(false);
    if (modalOpen) { setModalOpen(false); document.body.style.overflow = ""; }
    nav(`/${model.id}`);
  };

  // Shop handlers
  const handleRequestExtra = (sec: number) => {
    if (!sec || sec <= 0) { setShopOpen(true); return; }
    if (extraBalanceSec < sec) { setShopOpen(true); return; }
    setExtraBalanceSec((b) => b - sec);
    setExternalAddedSeconds((s) => s + sec);
  };
  const handleUseHint = () => {
    if (hintBalance <= 0) { setShopOpen(true); return false; }
    setHintBalance(hintBalance - 1);
    return true;
  };

  // Checkout flow
  const handleCheckout = (basket: Basket) => {
    setPaymentBasket(basket);
    setShopOpen(false);
    requestAnimationFrame(() => {
      setPaymentOpen(true);
      tg()?.HapticFeedback?.impactOccurred?.("medium");
    });
  };

  const handleClosePayment = () => setPaymentOpen(false);

  const handlePay = (basket: Basket) => {
    try {
      if (!basket) return;
      const hints = Number(basket.hintCount || 0);
      const time = Number(basket.timeSec || 0);
      if (hints > 0) setHintBalance(v => v + hints);
      if (time > 0) setExtraBalanceSec(v => v + time);
      setPaymentOpen(false);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      tg()?.showAlert?.("Purchase successful ✅");
    } catch (err) {
      console.error("[ModelApp] handlePay error:", err);
      tg()?.showAlert?.("Something went wrong while processing your purchase.");
    }
  };

  const progressLabel = `${progress}/${totalLevels}`;

  return (
    <>
      <Helmet>
        <title>{model.fullName} • {albumTitle} | Match Pairs</title>
      </Helmet>

      <div className="model-container" style={{ maxWidth: 900, margin: "0 auto" }}>

        <header className="status-bar status-bar--centered">
          <div className="safe-top-spacer" />
          <div className="status-inner">
            {/* Avatar is now inside the width-constrained block */}
            <img className="status-avatar" src={model.avatar} alt="avatar" />

            <div className="status-stack">
              {/* Row 1: progress bar + 0/8 on the same line */}
              <div className="progress-inline">
                <div
                  className="stepbar"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={totalLevels}
                  aria-valuenow={progress}
                >
                  {Array.from({ length: totalLevels }).map((_, i) => (
                    <span key={i} className={`step ${i < progress ? "done" : ""}`} />
                  ))}
                </div>
                <div className="progress-label progress-label--sm">{progressLabel}</div>
              </div>

              {/* Row 2: plain labels (non-clickable) */}
              <div className="status-metrics status-metrics--left">
                <span className="metric-pill metric-pill--sm">
                  ⏱️ {Math.min(999, Math.floor(extraBalanceSec / 60))}
                </span>
                <span className="metric-pill metric-pill--sm">
                  ✨ {Math.min(99, hintBalance)}
                </span>
              </div>
            </div>
          </div>
        </header>

        <Gallery images={imageEntries} progress={progress} onOpenLevel={startLevel} />

        <Modal
          open={modalOpen && playingIndex !== null}
          onClose={closeLevel}
          title={`Level ${activeIdx + 1} / ${totalLevels}`}
        >
          <div style={{ position: "relative" }}>
            <Confetti show={showConfetti} />
            <MemoryBoard
              key={`level-${activeIdx}`}
              imageUrl={imageEntries[activeIdx]?.clear ?? ""}
              blurredUrl={imageEntries[activeIdx]?.blurred ?? ""}
              levelIndex={activeIdx}
              totalLevels={totalLevels}
              rows={levelCfg.rows}
              cols={levelCfg.cols}
              timeLimitSec={levelCfg.timeLimitSec}
              onComplete={handleCompleteLevel}
              onNextLevel={goToNextLevel}
              initialFinished={solvedActive}
              externalAddedSeconds={externalAddedSeconds}
              availableExtraSec={extraBalanceSec}
              onRequestExtra={handleRequestExtra}
              availableHints={hintBalance}
              onUseHint={handleUseHint}
              paused={shopOpen || paymentOpen}
            />
          </div>
        </Modal>

        <div className="toolbar-spacer" aria-hidden="true" />
      </div>

      <Toolbar onRestartAll={handleRestartAll} onHome={handleHome} onOpenShop={() => setShopOpen(true)} />

      <ShopModal
        open={shopOpen}
        onClose={() => setShopOpen(false)}
        balanceSec={extraBalanceSec}
        balanceHints={hintBalance}
        onCheckout={handleCheckout}
      />
      <PaymentSheet
        open={paymentOpen}
        basket={paymentBasket}
        onClose={handleClosePayment}
        onPay={handlePay}
      />
    </>
  );
}
